import { Box, Flex, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import { SPECIALS } from '../../../common/constants';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import VideoArticlesGrid from '../../../components/VideoArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import {
  getVideoArticles,
  VideoArticle,
} from '../../../server/libs/videoArticles';
import withAuth from '../../../server/libs/withAuth';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import SpecialSideLoginBanner from '../../../components/SpecialSideLoginBanner';
import SideBarStickyContent from '../../../components/SideBarStickyContent';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import useSpecialPage from '../../../hooks/useSpecialPage';
import {
  findTagByTagName,
  getArticleIdsWithTagIds,
} from '../../../server/libs/tag';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';

export const JIHIKEN_SEARCH_TARGET_TAG_NAME =
  SPECIALS.privateHealthcare.tags[0];

export const JIHIKEN_PAGE_SLUG = 'private-healthcare';
export const JIHIKEN_PAGE_NAME = 'jihiken';
export const JIHIKEN_PAGE_PATH = `/special/${JIHIKEN_PAGE_SLUG}`;
export const JIHIKEN_PAGE_TITLE = '保険外診療特集';

const specialPagePath = JIHIKEN_PAGE_PATH;
const specialPageTitle = JIHIKEN_PAGE_TITLE;
const mainImagePath = '/images/special/private-healthcare_main_1300*720.png';
const specialPageSlug = JIHIKEN_PAGE_SLUG;
const userAccessHistoryOperation = `special_${JIHIKEN_PAGE_NAME}`;

type PageContext = {
  targetTag: Tag;
  articles: ArticleHeader[];
  videoArticles: VideoArticle[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

type Tag = {
  tagId: number | null | undefined;
  name: string | null | undefined;
};

// サイドバーにバナー等がある時はここにコンポーネントを入れる
const SideBanner = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  return (
    <>
      {!isAuthenticated && (
        <SideBarStickyContent stickyHeight={45}>
          <SpecialSideLoginBanner redirectUrl="/special/private-healthcare" />
        </SideBarStickyContent>
      )}
    </>
  );
};

const PrivateHealthcarePage: NextPage<PageContext> = ({
  targetTag,
  articles,
  videoArticles,
  isMobile,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isDemoAccount,
  isPrivate,
}) => {
  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageSlug,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
    needLogin: false,
    needLicenseVerified: false,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          {/* main start */}
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box pos="relative" mb={2}>
                <NextImage
                  src={mainImagePath}
                  alt={JIHIKEN_PAGE_TITLE}
                  width={1300}
                  height={720}
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    企画概要
                  </Text>
                  <Text fontSize="sm">
                    医療技術の進化による医療費の高騰や少子高齢化によって、皆保険制度の見直しなど、従来の保険診療だけでなく保険外診療の選択肢も増えてきております。
                  </Text>
                  <Text fontSize="sm">
                    こちらの特集ページでは保険外診療における「特別な医師や治療法」「マーケティングや経営」に関するスライドを公開中！
                  </Text>
                </Box>
              </Box>
            </Box>
            {/* main end */}
            {/* 3rd start */}
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {articles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      #{JIHIKEN_SEARCH_TARGET_TAG_NAME} に関するスライド
                    </Text>
                  </Flex>
                  <Flex direction={'column'}>
                    <ArticlesGrid
                      articles={articles}
                      isMobile={isMobile}
                      gaEventLabel={`${JIHIKEN_PAGE_NAME}_tagsearch`}
                      responsibleColumns={specialPageRegularColumns}
                      onShowThumbnail={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'show',
                          `special_${JIHIKEN_PAGE_NAME}#${articleId}`,
                        );
                      }}
                      onClick={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'click',
                          `special_${JIHIKEN_PAGE_NAME}#${articleId}`,
                        );
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
            {isAuthenticated && isLicenseVerified && (
              <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
                {videoArticles.length > 0 && (
                  <Box pt={2} pb={2}>
                    <Flex justifyContent="space-between" mb={1}>
                      <Text fontWeight="bold" fontSize="md">
                        #{JIHIKEN_SEARCH_TARGET_TAG_NAME} に関する動画
                      </Text>
                    </Flex>
                    <Flex direction={{ base: 'column', md: 'row' }}>
                      <VideoArticlesGrid
                        videoArticles={videoArticles}
                        isMobile={isMobile}
                        gaEventLabel={`${JIHIKEN_PAGE_NAME}_video`}
                        articleId={specialPageSlug}
                        maxColumns={2}
                      />
                    </Flex>
                  </Box>
                )}
              </Box>
            )}
          </GridItem>
          {/* 3rd end */}
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              <SideBanner isAuthenticated={isAuthenticated} />
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default PrivateHealthcarePage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    let articles: ArticleHeader[] = [];
    const targetTag = await findTagByTagName(JIHIKEN_SEARCH_TARGET_TAG_NAME);

    if (targetTag) {
      const articleIdList = await getArticleIdsWithTagIds(
        targetTag ? [targetTag.tag_id] : [],
      );

      const articleHeaders = await prisma.article_header.findMany({
        select: {
          article_id: true,
          name: true,
          article_type: true,
          status: true,
          top_img_m: true,
          session_count: true,
          like_count: true,
          disclose_scope: true,
          create_date: true,
          update_date: true,
          user_article_header_author_idTouser: true,
          article_tag_connection: {
            select: {
              tag: true,
            },
          },
          article_page: {
            select: {
              like_count: true,
            },
          },
          article_comments: {
            select: {
              id: true,
            },
          },
        },
        where: {
          article_id: {
            in: articleIdList,
          },
          status: 'DONE',
          hidden: false,
          is_deleted: false,
        },
        orderBy: {
          create_date: 'desc',
        },
      });
      // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
      const discloseIds = articleHeaders
        .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
        .filter((v) => !!v);
      // アカウント統合済みのユーザのプロフィールのみ返却される
      const idBaseProfileMap = await fetchAccountIntegrationProfiles(
        discloseIds,
      );

      articles = articleHeaders.reduce((articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: article.user_article_header_author_idTouser.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds:
              article.user_article_header_author_idTouser.update_date.getTime(),
            profilePhotoS3Key:
              article.user_article_header_author_idTouser.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      }, []);
    }

    const videoArticles = await getVideoArticles(specialPageSlug);

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        targetTag: {
          tagId: targetTag?.tag_id || null,
          name: targetTag?.tag_name || null,
        },
        articles,
        isMobile,
        isDemoAccount,
        videoArticles,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
